/**
 * PublicFrame — Read-only view of published public pages.
 * BlockNote editor removed (frame builder discontinued) — shows plain text content.
 */
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowRight02Icon as ChevronRight, Cancel01Icon as X, File01Icon as FileText, Home01Icon as Home, Menu01Icon as Menu, ViewIcon as Eye } from '@hugeicons/core-free-icons';
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface Frame {
  id: string;
  title: string;
  content: any;
  iconName?: string;
  iconColor?: string;
  customImageUrl?: string;
  bannerIndex?: number;
  bannerUrl?: string;
  bannerPosition?: number;
  parentId?: string | null;
  isPublished?: boolean;
}

function renderContent(content: any): string {
  if (!content) return '';
  if (typeof content === 'string') return content;
  // BlockNote JSON → plain text
  if (Array.isArray(content)) {
    return content.map((block: any) => {
      const text = block?.content?.map((c: any) => c?.text || '').join('') || block?.text || '';
      return text;
    }).filter(Boolean).join('\n\n');
  }
  if (content.content) return renderContent(content.content);
  return JSON.stringify(content);
}

export default function PublicFrame() {
  const { workspaceId, frameId } = useParams<{ workspaceId: string; frameId: string }>();
  const navigate = useNavigate();

  const [frame, setFrame] = useState<Frame | null>(null);
  const [allFrames, setAllFrames] = useState<Frame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    fetch(`/api/public/${workspaceId}/frames`)
      .then(r => r.ok ? r.json() : { frames: [] })
      .then(d => setAllFrames(d.frames || []))
      .catch(() => {});
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId || !frameId) return;
    setLoading(true);
    setError(null);
    fetch(`/api/public/${workspaceId}/frames/${frameId}`)
      .then(r => {
        if (!r.ok) throw new Error(r.status === 404 ? 'Page not found or not published' : 'Failed to load page');
        return r.json();
      })
      .then(d => setFrame(d.frame))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [workspaceId, frameId]);

  const renderSubpageTree = useMemo(() => (parentId: string | null, depth: number = 0): React.ReactNode => {
    const children = allFrames.filter(f => (f.parentId || null) === parentId && f.isPublished);
    return children.map(child => {
      const hasChildren = allFrames.some(f => f.parentId === child.id && f.isPublished);
      const isActive = child.id === frameId;
      return (
        <div key={child.id}>
          <button
            onClick={() => navigate(`/public/${workspaceId}/${child.id}`)}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors',
              isActive ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted',
              depth > 0 && 'ml-4'
            )}
          >
            <HugeiconsIcon icon={FileText} className="w-4 h-4 flex-shrink-0"  />
            <span className="flex-1 text-left truncate">{child.title}</span>
            {hasChildren && <HugeiconsIcon icon={ChevronRight} className="w-3 h-3"  />}
          </button>
          {hasChildren && renderSubpageTree(child.id, depth + 1)}
        </div>
      );
    });
  }, [allFrames, frameId, workspaceId, navigate]);

  if (loading) return (
    <div className="h-screen flex">
      <div className="w-64 border-r p-4 space-y-2">
        {[1,2,3].map(i => <Skeleton key={i} className="h-8 w-full" />)}
      </div>
      <div className="flex-1 p-8 space-y-4">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  );

  if (error || !frame) return (
    <div className="h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="text-6xl">404</div>
        <div className="text-xl font-medium">{error || 'Page not found'}</div>
        <p className="text-muted-foreground">This page may have been unpublished or deleted.</p>
        <Button onClick={() => navigate('/')}>
          <HugeiconsIcon icon={Home} className="w-4 h-4 mr-2"  /> Go Home
        </Button>
      </div>
    </div>
  );

  const plainText = renderContent(frame.content);

  return (
    <div className="h-screen flex bg-background">
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-background border border-border shadow-lg"
      >
        {isSidebarOpen ? <HugeiconsIcon icon={X} className="w-5 h-5"  /> : <HugeiconsIcon icon={Menu} className="w-5 h-5"  />}
      </button>

      {isSidebarOpen && (
        <div className="md:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setIsSidebarOpen(false)} />
      )}

      <div className={cn(
        'w-64 border-r border-border bg-sidebar flex flex-col',
        'fixed md:relative inset-y-0 left-0 z-40 transition-transform duration-200',
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      )}>
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-2 text-sm font-medium">
            <HugeiconsIcon icon={Eye} className="w-4 h-4"  />
            <span>Public View</span>
          </div>
        </div>
        <ScrollArea className="flex-1 p-3">
          <div className="space-y-1">
            <button
              onClick={() => navigate(`/public/${workspaceId}/${frameId}`)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium bg-primary/10 text-primary rounded-md"
            >
              <HugeiconsIcon icon={Home} className="w-4 h-4"  />
              {frame.title}
            </button>
            {renderSubpageTree(frameId || null)}
          </div>
        </ScrollArea>
        <div className="p-3 border-t border-border text-xs text-muted-foreground text-center">
          Powered by <span className="font-medium">GenofX</span>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <ScrollArea className="flex-1">
          <div className="max-w-4xl mx-auto px-8 py-12">
            <h1 className="text-4xl font-bold mb-8">{frame.title}</h1>
            <div className="prose prose-invert max-w-none text-foreground/80 whitespace-pre-wrap leading-relaxed">
              {plainText || <span className="text-muted-foreground italic">No content</span>}
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
